import { useMemo, useState } from "react";
import { Container, Row, Col, Card, Image, Form, Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetFitmentFacetsQuery } from "../slices/productsApiSlice";
import Loader from "../Components/Loader";
import Message from "../Components/Message";
import BackButton from "../Components/BackButton";
import Meta from "../Components/Meta";

const imgCandidates = (name) => {
  const slug = (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return [
    `/images/makes/${slug}.png`,
    `/images/makes/${slug}.jpg`,
    `/images/makes/${slug}.svg`,
  ];
};

export default function VehicleMakes() {
  const [sp] = useSearchParams();
  const category = sp.get("category") || "";
  const { data, isLoading, error } = useGetFitmentFacetsQuery(
    category ? { category } : {}
  );
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [letter, setLetter] = useState("");

  const makes = data?.makes || [];
  const sortedMakes = useMemo(() => [...makes].sort((a, b) => {
    const as = Number.isFinite(a?.sort) ? a.sort : Number.MAX_SAFE_INTEGER;
    const bs = Number.isFinite(b?.sort) ? b.sort : Number.MAX_SAFE_INTEGER;
    if (as !== bs) return as - bs;
    const an = (a?.make || '').localeCompare?.((b?.make || '')) ?? 0;
    return an;
  }), [makes]);

  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const filteredMakes = sortedMakes.filter((m) => {
    const makeName = (m?.make || "").trim();
    const matchesSearch = !search.trim() || makeName.toLowerCase().includes(search.trim().toLowerCase());
    const matchesLetter = !letter || makeName.toUpperCase().startsWith(letter);
    return matchesSearch && matchesLetter;
  });

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;

  return (
    <Container className="py-4">
      <Meta title="Shop Auto Parts by Vehicle Make | KCC Parts" canonicalPath="/makes" noindex={Boolean(category)} />
      <BackButton/>
      <h1>Choose a make</h1>
      {!sortedMakes.length && (
        <Message className="mt-3">
          {category
            ? `No makes are available for “${category}” right now.`
            : "No makes are available right now."}
        </Message>
      )}

      {sortedMakes.length > 0 && (
        <div className="my-3">
          <Form.Control
            type="search"
            placeholder="Search make..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search make"
            className="mb-2"
          />
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={!letter ? "primary" : "outline-primary"}
              onClick={() => setLetter("")}
            >
              All
            </Button>
            {letters.map((item) => (
              <Button
                key={item}
                size="sm"
                variant={letter === item ? "primary" : "outline-primary"}
                onClick={() => setLetter(item)}
              >
                {item}
              </Button>
            ))}
          </div>
        </div>
      )}
      {sortedMakes.length > 0 && !filteredMakes.length && (
        <Message className="mt-3">No makes found.</Message>
      )}
      <Row className="g-3 mt-2">
        {filteredMakes.map((m) => {
          const makeName = (m.make || "").trim();
          const fallbacks = imgCandidates(makeName);
          const imgSrc = (m.logo && m.logo.trim()) ? m.logo : fallbacks[0];
        
          return (
            <Col key={makeName || Math.random()} xs={12} sm={6} md={4} lg={3}>
              <Card
                role="button"
                className="part-card h-100"
                onClick={() => {
                  const params = new URLSearchParams({ make: makeName });
                  if (category) params.set("category", category);
                  navigate(`/vehicles?${params.toString()}`);
                }}
              >
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3 make-card">
                  {imgSrc && (
                    <div>
                      <Image
                        src={imgSrc}
                        alt={`${makeName} logo`}
                        className="make-logo"
                      />
                    </div>
                  )}
                  <div className="part-card-title">{makeName}</div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
}
